import { memo, useEffect } from 'react';
import { useWebApp } from '../webAppContext';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { NavigationBar } from './NavigationBar';
import { HomeScreen } from './HomeScreen';
import { Profile } from './Profile';
import { CreateFlow } from '../pages/CreateFlowPage';
import { CreationDetails } from './CreationDetails';
import { BalanceScreen } from './BalanceScreen';
import { SubscriptionPage } from '../pages/SubscriptionPage';
import { SettingsPage } from '../pages/SettingsPage';
import { ProjectsScreen } from './ProjectsScreen';
import { UserInfo } from '../types';

interface MainLayoutProps {
  userInfo: UserInfo;
  onBalanceUpdate: (newBalance: number) => void;
}

interface InitDataUnsafeStart {
  // Our custom param name
  startapp?: string;
  // Telegram WebApp start parameter
  start_param?: string;
}

export const MainLayout = memo(function MainLayout({ 
  userInfo, 
  onBalanceUpdate 
}: MainLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { initData, initDataUnsafe } = useWebApp();
  
  // Deep-link handling: navigate to creation details if `startapp=creation_{id}` is present
  useEffect(() => {
    // Check URL search param first
    const params = new URLSearchParams(location.search);
    let startapp = params.get('startapp');
    // Also try Telegram-provided start_param if not found
    if (!startapp && initDataUnsafe) {
      const data = initDataUnsafe as unknown as InitDataUnsafeStart;
      if (typeof data.startapp === 'string') {
        startapp = data.startapp;
      } else if (typeof data.start_param === 'string') {
        startapp = data.start_param;
      }
    }
    
    // Get the current path without any parameters
    const currentPath = location.pathname;
    
    // If we have already processed this startapp parameter, don't process it again
    const processedStartapp = sessionStorage.getItem('processed_startapp');
    
    // If we're already on a creation page, mark the startapp param as processed
    if (currentPath.startsWith('/creation/') && startapp) {
      sessionStorage.setItem('processed_startapp', startapp);
    }
    
    if (startapp && startapp.startsWith('creation_')) {
      // If we've already processed this startapp param, don't redirect
      if (processedStartapp === startapp) {
        return;
      }
      
      const id = startapp.split('creation_')[1];
      
      // Navigate to the creation and mark this startapp as processed
      sessionStorage.setItem('processed_startapp', startapp);
      navigate(`/creation/${id}`);
    }
  }, [location.search, navigate, initData, initDataUnsafe, location.pathname]);
  
  // Ensure navbar links don't trigger redirects back to creation page
  const handleNavigation = (path: string) => {
    // Clear any manual navigation flags
    sessionStorage.removeItem('manual_navigation');
    navigate(path);
  };

  // Hide navigation bar on certain screens
  const showNavBar = !location.pathname.startsWith('/create') && 
    !location.pathname.startsWith('/creation/') &&
    !location.pathname.startsWith('/balance') &&
    !location.pathname.startsWith('/subscription') &&
    !location.pathname.startsWith('/settings');

  return (
    <div className="app-container">
      <div className="content">
        <Routes>
          <Route path="/" element={<HomeScreen userInfo={userInfo} />} />
          <Route path="/projects/:page?" element={<ProjectsScreen userInfo={userInfo} />} />
          <Route path="/profile" element={<Profile userInfo={userInfo} />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/create" element={<CreateFlow onBalanceUpdate={onBalanceUpdate} />} />
          <Route path="/creation/:id" element={<CreationDetails onBalanceUpdate={onBalanceUpdate} />} />
          <Route path="/balance" element={<BalanceScreen userInfo={userInfo} />} />
          <Route path="/subscription" element={<SubscriptionPage userInfo={userInfo} />} />
          <Route path="/templates" element={<HomeScreen userInfo={userInfo} />} />
          <Route path="/inbox" element={<HomeScreen userInfo={userInfo} />} />
        </Routes>
      </div>
      {showNavBar && <NavigationBar onNavigate={handleNavigation} />}
    </div>
  );
});